import {
  SignupAlertError,
  SignupAlertErrorType,
  SignupDialogError,
  SignupDialogErrorType,
  SignupErrorType,
} from 'features/signUp/types';
import { useDispatch } from 'react-redux';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useCobrand, useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import {
  hideAlertError,
  hideDialogError,
  resetSignUpState,
  setSignUpState,
} from '../../signUpSlice';

// Builder class for creating signup dialog errors
class SignupDialogErrorBuilder {
  private error: Partial<SignupDialogError> = {};

  constructor(
    private dispatch: any,
    private translation: (key: string, params?: any) => string,
    private navigate: (path: string) => void,
    private openHelpDialog: () => void,
  ) {
    // Set default onClose behavior - can be overridden if needed
    this.error.onClose = () => this.dispatch(hideDialogError());
  }

  type(type: SignupDialogErrorType): this {
    this.error.type = type;
    return this;
  }

  title(key: string, params?: any): this {
    this.error.title = this.translation(key, params);
    return this;
  }

  body(key: string): this {
    this.error.body = this.translation(key);
    return this;
  }

  helpButton(labelKey: string = 'get_help'): this {
    this.error.helpElement = {
      type: 'button',
      label: this.translation(labelKey),
      onClick: () => this.openHelpDialog(),
    };
    return this;
  }

  helpLink(displayTextKey: string, urlKey: string): this {
    this.error.helpElement = {
      type: 'link',
      displayText: this.translation(displayTextKey),
      url: this.translation(urlKey),
    };
    return this;
  }

  // Primary button that dismisses the dialog by default
  primaryButton(labelKey: string): this {
    this.error.primaryCtaButton = {
      label: this.translation(labelKey),
      onClick: () => this.dispatch(hideDialogError()),
    };
    return this;
  }

  // Secondary button that navigates to login
  loginButton(labelKey: string = 'login'): this {
    this.error.secondaryCtaButton = {
      label: this.translation(labelKey),
      onClick: () => {
        this.dispatch(resetSignUpState());
        this.navigate('/login');
      },
    };
    return this;
  }

  // Custom secondary button with custom action
  secondaryButton(labelKey: string, onClick: () => void): this {
    this.error.secondaryCtaButton = {
      label: this.translation(labelKey),
      onClick,
    };
    return this;
  }

  build(): SignupDialogError {
    const requiresHelpElement = this.error.type !== 'AccessRestricted';
    if (
      !this.error.title ||
      (requiresHelpElement && !this.error.helpElement) ||
      !this.error.primaryCtaButton ||
      !this.error.onClose
    ) {
      throw new Error('SignupDialogError is missing required fields');
    }
    return this.error as SignupDialogError;
  }
}

export const useSignupErrorFactory = (openHelpDialog: () => void) => {
  const storeDispatch = useDispatch();
  const { translation } = useTranslationContext();
  const { cobrand } = useCobrand();
  const navigate = useNavigate();

  // Factory method for creating a builder instance
  const createDialogBuilder = () =>
    new SignupDialogErrorBuilder(storeDispatch, translation, navigate, openHelpDialog);

  const createSignupAlertError = (error: SignupAlertErrorType): SignupAlertError => {
    const handleDismiss = () => storeDispatch(hideAlertError());

    switch (error) {
      case 'NoNetwork':
        return {
          message: translation('error_loading_body'),
          position: 'top',
          handleDismiss,
        };
      default:
        return {
          message: translation('generic_error_occurred'),
          position: 'top',
          handleDismiss,
        };
    }
  };

  const isSignupAlertError = (error: SignupErrorType): error is SignupAlertErrorType =>
    error === 'NoNetwork';

  const isSignupDialogError = (error: SignupErrorType): error is SignupDialogErrorType =>
    error === 'AccessRestricted' ||
    error === 'EmailAlreadyRegistered' ||
    error === 'UserCreationFailed' ||
    error === 'Generic' ||
    error === 'IneligibleUser' ||
    error === 'IneligibleSSOUser' ||
    error === 'InvalidAccessCode' ||
    error === 'UnderageUser' ||
    error === 'UserEnrollmentFailed';

  const createSignupDialogError = (error: SignupDialogErrorType): SignupDialogError => {
    const errorType = isSignupDialogError(error) ? error : 'Generic';
    const builder = createDialogBuilder().type(errorType);
    switch (error) {
      case 'AccessRestricted':
        return builder
          .title('web_ineligible')
          .body('web_ineligible_access_code')
          .primaryButton('general_ok')
          .build();

      case 'EmailAlreadyRegistered':
        return builder
          .title('email_in_use_error_header')
          .body('email_in_use_error_body')
          .helpButton('unsure_get_help')
          .primaryButton('try_again')
          .loginButton()
          .build();

      case 'UserCreationFailed':
        return builder
          .title('registration_error_header')
          .body('something_has_gone_wrong')
          .helpButton()
          .primaryButton('try_again')
          .build();

      case 'IneligibleSSOUser':
        return builder
          .title('sso_eligibility_failure_title', { arg1: cobrand.displayName })
          .body('sso_eligibility_failure_body')
          .helpButton()
          .primaryButton('general_ok')
          .build();

      case 'IneligibleUser':
        return builder
          .title('eligibility_error_header')
          .body('eligibility_error_body')
          .helpLink('eligibility_error_support_link_display', 'eligibility_error_support_link_url')
          .primaryButton('try_again')
          .build();

      case 'InvalidAccessCode':
        return builder
          .title('access_code_error_header')
          .body('access_code_error_body')
          .helpLink('access_code_error_support_link_display', 'access_code_error_support_link_url')
          .primaryButton('try_again')
          .build();

      case 'UserEnrollmentFailed':
        return builder
          .title('something_has_gone_wrong')
          .body('enrollment_error_text_without_error_code')
          .helpButton()
          .primaryButton('try_again')
          .build();

      case 'UnderageUser':
        return builder
          .title('eligibility_dob_error_header')
          .body('eligibility_dob_error_body')
          .helpButton()
          .primaryButton('general_ok')
          .build();

      case 'Generic':
      default:
        return builder
          .title('something_has_gone_wrong')
          .body('')
          .helpButton()
          .primaryButton('try_again')
          .build();
    }
  };

  return {
    isSignupAlertError,
    createSignupAlertError,
    isSignupDialogError,
    createSignupDialogError,
  };
};
